import { IResDataElasticDefault } from '@core/models';
import { BotBlock, BotCard, BotGroup, BotItem, CommentCondition } from './chatbot.model';

// ───────────────────────────── REQUEST ─────────────────────────────

/** V2: POST /chatbot/script/list danh sách chatbot */
export interface IListBotParams {
    businessId: string | number;
    channel?: string;
    fanpageId?: string | number;
    fanpageIds?: Array<string | number>;
    ids?: string[];
    size?: number;
    next?: string;
}

/** V2: POST /chatbot/script/addgroup */
export interface IAddGroupParams {
    botId: string;
    name?: string;
    type: 'keyword' | 'script' | string;
    order?: number;
}

/** V2: POST /chatbot/script/addbot */
export interface IAddBotParams {
    name: string;
    channel: number | string;
    cloneBotId?: string;
    fanpageId?: string | string[];
    isReplaceBot?: number | boolean | null;
    businessId?: string | number;
}

/** V2: POST /chatbot/script/addblock */
export interface IAddBlockParams {
    botId: string;
    groupId: string;
    name?: string;
    order?: number;
}

/** V2: POST /chatbot/script/addcard */
export interface IAddCardParams {
    botId: string;
    settings?: string;
    active?: number;
    groupId?: string | null;
    type?: string;
    order?: number;
    blockId?: string | null;
}

/** V2: POST /chatbot/script/updatecard */
export interface IUpdateCardParams {
    id: string;
    order?: number | string;
    active?: number;
    settings?: string;
}

/** V2: POST /chatbot/script/cloneblockgroup */
export interface ICloneBlockGroupParams {
    botId: string;
    type: number; // 1=group, 2=block
    id: string;
    groupId?: string;
}

export enum CampaignStatus {
    ACTIVE = 1,
    INACTIVE = 2,
}

/** V2: POST /chatbot/campaign/list */
export interface IListCampaignParams {
    botId: string;
    id?: string;
    campaignId?: string;
    status?: string | number;
    name?: string;
    next?: string;
    size?: number;
}

/** V2: POST /chatbot/campaign/listlogs */
export interface IListCampaignLogsParams {
    botId: string;
    campaignId?: string;
    startTime?: number;
    endTime?: number;
    next?: string;
    size?: number;
}

/** V2: POST /chatbot/campaign/addlog */
export interface IAddLogCampaignParams {
    campaignId: string;
    botId: string;
    name: string;
    message: string;
}

/** V2: POST /chatbot/campaign/sendmessage */
export interface ISendMessageCampaignParams {
    campaignId: string;
    blockId: string;
    logId: string;
    next?: string;
}

export interface ISendMessageCampaignData {
    countItem: number;
    messages?: Array<{ fanpageUserId: string; message: string }>;
    countError?: number;
    countSuccess?: number;
    next?: any[] | null;
}

/** V2: POST /chatbot/campaign/updatecampaignuser */
export interface IUpdateCampaignUserParams {
    dataUpdate: Array<{
        id: string;
        campaignId?: string | string[];
        status?: string | number;
    }>;
}

/** V2: POST /chatbot/campaign/listuser */
export interface IListCampaignUserParams {
    campaignId?: string;
    pageId?: string;
    status?: string | number;
    fanpageUserId?: string | string[];
    startTime?: number;
    endTime?: number;
    botId?: string;
    export?: string | number | boolean;
    size?: number;
    next?: string;
}

export interface IPostManageParams {
    pageIds: string[];
    size?: number;
    afterJson?: string;
    isLive?: number | boolean;
    isReels?: number | boolean;
    isNormal?: number | boolean;
    hasAd?: number | boolean;
    postId?: string;
    startDate?: number;
    endDate?: number;
    fields?: string;
    pullComments?: boolean;
    pageToken?: Record<string, string>;
}

/** Các loại bài viết lọc được ở BE — '' là không lọc */
export type TPostTypeFilter = '' | 'isLive' | 'isReels' | 'isNormal' | 'hasAd';

/** Item bài viết */
export interface IPostManageItem {
    id: string;
    fanpageId?: string;
    createdTime?: number;
    postCreatedAt?: number;
    message?: string;
    story?: string;
    attachments?: { data?: Array<{ media?: { image?: { src?: string } } }> };
    /** BE trả 1/0 */
    isLive?: number;
    isReels?: number;
    /** BE trả boolean ở API v2 (khác isLive/isReels) */
    hasAd?: boolean | number;
    isDeleted?: boolean;
    permalink?: string;
    [key: string]: any;
}

/** V2: POST /chatbot/script/pushscripttometa */
export interface ISendScriptToMetaParams {
    pageId: string | string[];
    cardId: string;
    welcome_message_flow: string;
    name?: string;
}

// ───────────────────────────── RESPONSE ─────────────────────────────

/** Một item page trả về từ getPageInfo */
export interface IPageInfo {
    id?: string;
    name?: string;
    avatar?: string;
}

/** Response data của listBot v2 */
export type IListBotResponse = IResDataElasticDefault<BotItem[]>;

export type IListCommentResponse = IResDataElasticDefault<CommentCondition[]>;

export type IUpdateCommentResponse = CommentCondition;

/** Chi tiết bot trả về từ loadChatbot */
export interface ILoadChatbotResponse {
    item: BotItem;
    groups: BotGroup[];
    blocks: BotBlock[];
    cards: BotCard[];
    error?: string;
}

/** 1 voucher Shopee/Lazada */
export interface IVoucherItem {
    id: string;
    code: string;
    name: string;
    voucherType: number;     // 1: Shop, 2: Sản phẩm
    discountType: number;    // 1: fix_amount, 2: discount_percentage, 3: coin_cashback
    discountAmount: number;
    percentage: number;
    percentUsage: number;
    showDate: string;
}

export interface IGetVoucherListResponse {
    items: IVoucherItem[];
}

/** V2: POST /chatbot/script/scriptapitest — data { response } (docs scriptapitest.md) */
export interface IScriptApiTestResponse {
    response: unknown;
}

// ───────────── RESPONSE THEO DOCS docs/Module/Chatbot ─────────────

/** data { success } — removebot/removeblock/removecard/removegroup/removeconnectpage/updateblock */
export interface ISuccessData {
    success: boolean;
}

/**
 * addbot/updatebot: data { item } là bot vừa lưu.
 * Nếu page đang gán bot khác và chưa truyền isReplaceBot: data { type: 'page_connected' }.
 */
export interface IBotMutationData {
    item?: BotItem;
    type?: string;
    error?: string;
}

/** Response data bọc một item, dùng chung cho các API thêm block/card/group/message. */
export interface IItemData<T> {
    item?: T;
}

/** updatecard: card đã cập nhật nằm trong data.data (docs updatecard.md) */
export interface IUpdateCardData {
    data?: BotCard;
}

/** listgroup: data { result } (docs listgroup.md) */

export interface IListGroupData {
    result: BotGroup[];
}

/** cloneblockgroup: type=1 item là group mới, type=2 item là block mới (docs cloneblockgroup.md) */
export interface ICloneBlockGroupData {
    item?: BotBlock | BotGroup;
    blocks?: BotBlock[];
    cards?: BotCard[];
}

/** pushscripttometa: data { item, flowIds, errors, summary } (docs pushscripttometa.md) */
export interface ISendScriptToMetaData {
    item?: BotCard;
    flowIds?: Record<string, string>;
    errors?: Record<string, string>;
    summary?: {
        total: number;
        updated: number;
        created: number;
        success: number;
        failed: number;
    };
}

/** Message của script — getmessage: data là chính message object, rỗng nếu không thấy (docs getmessage.md) */
export interface IScriptMessageItem {
    id?: string;
    businessId?: string | number;
    name?: string;
    createdById?: number | string | null;
    createdAt?: number;
    content?: string | Record<string, unknown> | null;
    settings?: Record<string, unknown> | null;
}

/** updatemessage: data { message } (docs updatemessage.md) */
export interface IUpdateMessageData {
    message: boolean;
}

/** 1 campaign (docs Campaign/list.md) */
export interface ICampaignItem {
    id: string;
    name: string | null;
    botId: string;
    status: number | null;
    createdTime: number | null;
    countActive?: number;
}

/** 1 user trong campaign (docs Campaign/listuser.md) */
export interface ICampaignUserItem {
    id: string;
    campaignId: string | string[];
    fanpageId: string | null;
    fanpageUserId: string | null;
    fanpageUserName: string | null;
    status: number | null;
    notificationMessagesToken?: string | null;
    tokenExpiryTime?: number | null;
    createdTime: number | null;
    email: string;
}

/** 1 user khi export danh sách campaign (docs Campaign/listuser.md) */
export type TCampaignUserExportItem = {
    id?: string;
    pageId?: string | null;
    fanpageId?: string | null;
    fanpageUserId: string | null;
    fanpageUserName: string | null;
    email: string;
    campaignId: string | string[];
    status: number | string | null;
    createdTime: number | string | null;
};

/** 1 log gửi tin campaign (docs Campaign/listlogs.md, addlog.md — addlog trả log trực tiếp trong data) */
export interface ICampaignLogItem {
    id: string;
    name: string | null;
    campaignId: string | null;
    botId: string | null;
    message?: Record<string, unknown> | string | null;
    countSuccess: number | null;
    countFailed: number | null;
    createdById: number | null;
    createdByName: string | null;
    createdTime: number | null;
}

/** countuser: data { result: [{id, total, active}] } (docs Campaign/countuser.md) */
export interface ICampaignUserCountData {
    result: { id: string; total: number; active: number }[];
}

